/**
 * The client's Сделки: what is open and what has closed, in plain words
 * (owner, 2026-10-15). Coin, result, how long — no Score, ranks, versions,
 * pools or leverage. The figures are the same the owner's book shows, read
 * from the same overview and history (one source per figure).
 *
 * Nothing scrolls sideways: rows are cards on a phone and columns from lg.
 */
import { useEffect, useMemo, useState } from "react";

import { PageHeader } from "@/design-system/PageHeader";
import { Panel } from "@/design-system/Panel";
import type { CycleResponse } from "@/shared/api";
import { isNegative, isZero, signedMoney, signedPercent, toPlaces } from "@/shared/money/money";
import { useOverview } from "@/shared/overview/useOverview";
import { ShareButton } from "@/shared/share/ShareButton";
import { TradesTerminal } from "@/shared/terminal/TradesTerminal";
import { humanAge } from "@/shared/time/age";

import { NEWEST_FIRST, useHistory } from "../cycles/useHistory";

const ROW =
  "grid grid-cols-2 gap-x-4 gap-y-1 border-b border-border-subtle py-3 last:border-b-0 " +
  "lg:grid-cols-[minmax(8rem,1.4fr)_repeat(3,minmax(0,1fr))] lg:items-center";
const HEAD = "hidden text-label-caps text-text-muted lg:grid";

function tone(value: string): string {
  const shown = toPlaces(value);
  if (isZero(shown)) return "text-text-secondary";
  return isNegative(shown) ? "text-status-negative" : "text-status-positive";
}

/** The label a card carries where the header row is not shown. */
function Cell({ label, children, end = false }: {
  readonly label: string; readonly children: React.ReactNode; readonly end?: boolean;
}): React.JSX.Element {
  return (
    <div className={`flex flex-col ${end ? "lg:items-end lg:text-right" : ""}`}>
      <span className="text-helper-sm text-text-muted lg:hidden">{label}</span>
      <span className="font-numeric tabular-nums text-text-primary">{children}</span>
    </div>
  );
}

function day(iso: string): string {
  return new Date(iso).toLocaleDateString("ru-RU", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function ClosedDeals({ cycles, total }: {
  readonly cycles: readonly CycleResponse[]; readonly total: number;
}): React.JSX.Element {
  return (
    <Panel title={`Закрытые сделки · ${String(total)}`} testId="client-closed-deals">
      {cycles.length === 0 ? (
        <p className="text-body-main text-text-secondary">Закрытых сделок пока нет.</p>
      ) : (
        <div>
          <div className={`${ROW} ${HEAD}`}>
            <span>Монета</span>
            <span className="text-right">Заработано</span>
            <span className="text-right">Длилась</span>
            <span className="text-right">Закрыта</span>
          </div>
          {cycles.map((cycle) => (
            <div key={cycle.cycle_id ?? `${cycle.symbol}-${cycle.closed_at}`} className={ROW} data-testid="client-closed-deal">
              <span className="col-span-2 flex items-center justify-between gap-2 font-medium text-text-primary lg:col-span-1">
                {cycle.symbol}
                <ShareButton
                  card={{ kind: "deal", symbol: cycle.symbol, rate: cycle.rate, net: cycle.net, heldSeconds: cycle.held_seconds, closedAt: cycle.closed_at }}
                  name={`skryptos-${cycle.symbol}`}
                  label="Поделиться сделкой"
                />
              </span>
              <Cell label="Заработано" end>
                <span className={tone(cycle.net)}>
                  {signedMoney(cycle.net)}
                  <span className="ml-2 text-helper-sm">{signedPercent(cycle.rate)}</span>
                </span>
              </Cell>
              <Cell label="Длилась" end>{humanAge(cycle.held_seconds)}</Cell>
              <Cell label="Закрыта" end>{day(cycle.closed_at)}</Cell>
            </div>
          ))}
        </div>
      )}
      <p className="mt-3 text-helper-sm text-text-secondary">
        {total > cycles.length
          ? `Последние ${String(cycles.length)} из ${String(total)} за ${String(WINDOW_DAYS)} дней.`
          : `За последние ${String(WINDOW_DAYS)} дней.`}
      </p>
    </Panel>
  );
}

/** The last ninety days. */
const WINDOW_DAYS = 90;
/** How often the window moves on. */
const STEP_MS = 30_000;

/**
 * The last ninety days, up to the next step. The end used to be fixed when the
 * page opened, so a deal that closed while it was open left «Открытые» and
 * never reached «Закрытые» (audit, 2026-10-16). It moves on every thirty
 * seconds, and stays put in between so the read is stable.
 */
export function lastDays(now: number): { readonly since: string; readonly until: string } {
  const until = (Math.floor(now / STEP_MS) + 1) * STEP_MS;
  return {
    since: new Date(until - WINDOW_DAYS * 86_400_000).toISOString(),
    until: new Date(until).toISOString(),
  };
}

function useStep(): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    const timer = setInterval(() => { setNow(Date.now()); }, STEP_MS);
    return () => { clearInterval(timer); };
  }, []);
  return now;
}

export function ClientDeals(): React.JSX.Element {
  const overview = useOverview();
  const step = useStep();
  const range = useMemo(() => lastDays(step), [step]);
  const history = useHistory(range, 0, NEWEST_FIRST);
  return (
    <div className="flex flex-col gap-2" data-testid="client-deals">
      <PageHeader title="Сделки" description="Что открыто сейчас и что уже закрыто. Суммы в USDT, после комиссий биржи." />
      {overview.status === "ready" && overview.overview.stale && (
        <p role="status" className="text-helper-sm text-status-warning" data-testid="client-deals-stale">
          Данные не обновляются — показано последнее известное.
        </p>
      )}
      {overview.status === "ready" ? (
        <TradesTerminal overview={overview.overview} simple />
      ) : (
        <Panel title="Открытые сделки" testId="client-open-deals"><p role="status" className="text-body-main text-text-secondary">
          {overview.status === "loading" ? "Загружаю…" : "Сейчас недоступно. Обновите страницу."}
        </p></Panel>
      )}
      {history.status === "ready" ? (
        <ClosedDeals cycles={history.history.cycles} total={history.history.total} />
      ) : (
        <Panel title="Закрытые сделки" testId="client-closed-deals"><p role="status" className="text-body-main text-text-secondary">
          {history.status === "loading" ? "Загружаю…" : "Сейчас недоступно. Обновите страницу."}
        </p></Panel>
      )}
    </div>
  );
}
